import type { PageExperience } from '@/features/experiences/types';
import { getEventFeed } from '@/features/sources/lib/feed.lib';
import type { DecoratedEvent } from '../types';
import { isExperienceEvent } from './event.lib';

// An event's emoji resolves through three tiers, highest priority first:
//
//   1. metadata.emoji — this experience's per-event override. An experience
//      consuming an event through a feed can't write the event's own emoji (the
//      server rejects updateEvent for a foreign origin), so it writes one here.
//   2. the feed's default emoji — set in Sources panel → Source defaults, and
//      applied across every event the feed brings in.
//   3. event.emoji — the event's own, which travels with it to every experience
//      that consumes it.
//
// The feed default outranking the event's own emoji is the point of the control,
// not an accident: an experience owner assigning one is styling a whole calendar
// at once, and a feed whose events arrive carrying their author's emoji would
// otherwise ignore them. An individual event is re-emojied by overriding it at
// tier 1, which is what the edit modal writes. This is also how the schedule-side
// model behaves — updateFeedForSchedule stamps a new feed default onto
// EntryToSchedule.emoji (its tier 1) for every entry not individually changed, so
// there too the default wins over Entry.emoji. Note that its read-time helper
// (server lib/entry.ts pickEmoji) reads own-then-feed: the stamping is what makes
// the default effective, so don't take that ordering as the rule. The server
// documents this in services/event/effectiveMetadata.ts.
//
// Contrast a feed's default *category* and *labels*, where the event's own win —
// those are classifications the event asserts about itself, and a feed default
// only fills a gap.
//
// Display only. Assignment surfaces must not write what this returns back onto
// the event — that would bake an inherited default in as the event's own. The
// edit modal is the exception that proves the rule: it displays this value but
// routes the write by origin (see ControlledMetadataPickers' handleKindChange
// for the same split on shading).

// `'metadata' in event` rather than a cast: chat and magic-job events, and some
// printable fixtures, carry no metadata at all.
export const getDisplayEmoji = (
  experience: PageExperience,
  event: DecoratedEvent
): string | null => {
  const override = 'metadata' in event ? event.metadata.emoji : null;
  if (override) {
    return override;
  }

  const feedDefault = isExperienceEvent(event)
    ? getEventFeed(experience, event)?.emoji
    : null;

  return feedDefault ?? event.emoji ?? null;
};
